// What the payment pages make of a Stripe answer: the result of confirming
// a payment in the page, and the query Stripe (or the API, for a hosted
// Checkout) brings the customer back with after a redirect. Pure.

/** The PaymentIntent statuses that mean the customer has paid: "succeeded"
 * (captured), "requires_capture" (a hold: authorised now, captured when the
 * school confirms) and "processing" (a bank method still settling). */
const PAID_STATUSES = new Set(["succeeded", "requires_capture", "processing"]);

const GENERIC_FAILURE = "Your payment didn’t go through. Please try again or use another card.";

/** The outcome of `stripe.confirmPayment(…, redirect: "if_required")` when
 * it comes back to the page (no redirect). A validation error is shown by
 * the payment form itself, so it carries no message of its own. */
export function confirmOutcome(result: {
  error?: { type?: string; message?: string } | null;
  paymentIntent?: { id?: string; status?: string } | null;
}): { kind: "paid"; paymentIntentId?: string } | { kind: "error"; message: string | null } {
  if (result.error) {
    if (result.error.type === "validation_error") return { kind: "error", message: null };
    return { kind: "error", message: result.error.message || GENERIC_FAILURE };
  }
  const status = result.paymentIntent?.status;
  if (status && PAID_STATUSES.has(status)) return { kind: "paid", paymentIntentId: result.paymentIntent?.id };
  return { kind: "error", message: GENERIC_FAILURE };
}

/** The query keys a return adds, which the page drops from its address once
 * read (so a reload doesn't show the outcome again). */
const RETURN_KEYS = [
  "payment_intent",
  "payment_intent_client_secret",
  "redirect_status",
  "success",
  "baluu_checkout",
  "basket_order_id",
  "checkout_session_id",
];

export type ReturnState = {
  /** "paid": Stripe (or v1's `success` flag) says it went through;
   * "failed": the bank or card refused it; "cancelled": the customer left
   * a hosted Checkout. */
  outcome: "paid" | "failed" | "cancelled" | null;
  paymentIntentId?: string;
};

/** Reads a return from the page's query. */
export function readReturn(search: string): ReturnState {
  const query = new URLSearchParams(search);
  const paymentIntentId = query.get("payment_intent") || undefined;
  const redirect = query.get("redirect_status");
  if (query.get("baluu_checkout") === "cancelled") return { outcome: "cancelled" };
  if (redirect) return { outcome: PAID_STATUSES.has(redirect) ? "paid" : "failed", ...(paymentIntentId ? { paymentIntentId } : {}) };
  if (query.has("success") || query.get("baluu_checkout") === "success") return { outcome: "paid", ...(paymentIntentId ? { paymentIntentId } : {}) };
  return { outcome: null };
}

/** The query without a return's keys ("" when nothing else is left). */
export function withoutReturn(search: string): string {
  const query = new URLSearchParams(search);
  for (const key of RETURN_KEYS) query.delete(key);
  const rest = query.toString();
  return rest ? `?${rest}` : "";
}

/** What the page says about a refused redirect payment. */
export const RETURN_FAILED = "Your payment didn’t go through. Please try again, or use another way to pay.";
export const RETURN_CANCELLED = "You left the payment page before paying. You can pay below whenever you’re ready.";

/** Sends the whole page (the top window, when the site is framed) to a
 * Stripe-hosted Checkout. */
export function goToHostedCheckout(url: string): void {
  try {
    (window.top ?? window).location.assign(url);
  } catch {
    window.location.assign(url);
  }
}

/** How long a "paid, not showing yet" mark keeps a record's Pay hidden in
 * this tab (a stuck payment is then the school's to sort out, not a second
 * charge). */
export const PAID_MARK_TTL_MS = 30 * 60 * 1000;

/** A payment mark as stored: when it was made, and what the record looked
 * like before paying (the secure payment page's baseline; null otherwise). */
export type PaymentMark<B> = { at: number; baseline: B | null };

export const serializeMark = <B>(baseline: B | null, now: number) => JSON.stringify({ at: now, baseline });

/** A stored mark, unless it's missing, unreadable or older than the TTL. */
export function parseMark<B>(raw: string | null, now: number, parseBaseline: (value: unknown) => B | null): PaymentMark<B> | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as { at?: unknown; baseline?: unknown };
    if (typeof value?.at !== "number" || now - value.at > PAID_MARK_TTL_MS || value.at > now + 60_000) return null;
    return { at: value.at, baseline: value.baseline == null ? null : parseBaseline(value.baseline) };
  } catch {
    return null;
  }
}

/** The body that records a payment the customer came back from, as the
 * basket's return handler sends it: a card return names the intent, a
 * hosted Checkout's names its session. Null when the return names neither
 * (nothing to record). */
export function recordReturnBody(basketOrderId: string, search: string): { basketOrderId: string; paymentIntentId?: string; checkoutSessionId?: string } | null {
  const query = new URLSearchParams(search);
  const paymentIntentId = query.get("payment_intent") || undefined;
  const checkoutSessionId = query.get("checkout_session_id") || undefined;
  const named = query.get("basket_order_id");
  // A return for another order than this page's isn't this page's to record.
  if (named && named !== basketOrderId) return null;
  if (query.get("baluu_checkout") === "success") return { basketOrderId, ...(checkoutSessionId ? { checkoutSessionId } : {}) };
  if (paymentIntentId && query.get("redirect_status") !== "failed") return { basketOrderId, paymentIntentId };
  return null;
}
